<script setup lang="ts">
import {computed, ref} from "vue";
import {useEventListener} from "@vueuse/core";
import LegacyAccess from "@/core/interfaces/Access";
import {TeamTask, TeamTaskStatus} from "@/core/interfaces/TeamTask";
import {TASK_COLUMNS, nextStatus, previousStatus} from "@/team-task/taskBoard";
import TeamTaskCard from "@/team-task/components/TeamTaskCard.vue";
import {Archive} from "@lucide/vue";

/** The last column of the design: the only one archiving empties. */
const COMPLETED_STATUS = TASK_COLUMNS[TASK_COLUMNS.length - 1].status;

const DRAG_THRESHOLD_PX = 4;

const {tasks, members, draggable = true} = defineProps<{
    tasks: TeamTask[];
    members: LegacyAccess[];
    /**
     * Off while a filter hides part of a column: the drop payload names the
     * column's whole content, and computing it from a partial view would
     * silently reshuffle the cards the filter is hiding.
     */
    draggable?: boolean;
}>();

const emits = defineEmits<{
    (event: 'open', task: TeamTask): void;
    (event: 'claim', task: TeamTask): void;
    (event: 'move', payload: { task: TeamTask; status: TeamTaskStatus }): void;
    (event: 'reorder', payload: { status: TeamTaskStatus; taskIds: number[] }): void;
    (event: 'archive-completed'): void;
}>();

/** Column boxes, hit-tested to find which one a drag is released over. */
const columnElements = ref<(HTMLElement | null)[]>([]);

const setColumnElement = (element: unknown, index: number) => {
    columnElements.value[index] = (element as HTMLElement | null) ?? null;
};

/** Card boxes, hit-tested to find which gap a drop lands in. */
const cardElements = ref<Record<number, HTMLElement | null>>({});

const setCardElement = (taskId: number, element: unknown) => {
    cardElements.value[taskId] = (element as HTMLElement | null) ?? null;
};

const drag = ref<{
    task: TeamTask;
    originColumn: number;
    targetColumn: number;
    /** Slot the card would land in, counted among the cards left in place. */
    insertIndex: number;
    moved: boolean;
    startX: number;
    startY: number;
    /** Live pointer position, and where inside the card it was grabbed. */
    pointerX: number;
    pointerY: number;
    offsetX: number;
    offsetY: number;
    width: number;
} | null>(null);

const knownStatuses = TASK_COLUMNS.map(column => column.status);

/**
 * The three columns of the design, plus one per unrecognised status.
 *
 * The status values are provisional and will be frozen against the real
 * mockup: filtering on the known three alone would make every card carrying a
 * renamed status vanish from the board — no column, no count, no warning —
 * while it still exists in the database. Dropping such a card into a real
 * column is also how a pharmacy recovers it.
 */
const columns = computed(() => {
    const strays = [...new Set(
        tasks
            .map(task => task.status)
            .filter(status => !knownStatuses.includes(status))
    )];

    return [
        ...TASK_COLUMNS.map(column => ({...column, droppable: true})),
        // Shown so nothing disappears, but never a drop target: moving a card
        // into one would stamp it with a status the server rejects.
        ...strays.map(status => ({status, label: `Statut inconnu (${status})`, droppable: false}))
    ].map(column => ({
        ...column,
        tasks: tasks.filter(task => task.status === column.status)
    }));
});

/** The column under the pointer, or the origin one when outside the board. */
const columnIndexAt = (clientX: number, fallback: number) => {
    const index = columnElements.value.findIndex(element => {
        if (!element) {
            return false;
        }

        const rect = element.getBoundingClientRect();

        return clientX >= rect.left && clientX <= rect.right;
    });

    return index === -1 ? fallback : index;
};

/**
 * The gap the card would drop into: the number of cards, the dragged one
 * aside, whose middle line the pointer has already passed.
 */
const insertionIndexAt = (columnIndex: number, clientY: number, draggedId: number) => {
    const column = columns.value[columnIndex];

    if (!column) {
        return 0;
    }

    return column.tasks
        .filter(task => task.id !== draggedId)
        .filter(task => {
            const rect = cardElements.value[task.id]?.getBoundingClientRect();

            return !!rect && clientY > rect.top + rect.height / 2;
        })
        .length;
};

/**
 * `pointerup` fires before `click`, so the drag state is already cleared when
 * the click handler runs: a dedicated flag is what keeps a drop from also
 * opening the detail modal.
 *
 * It is cleared on the next `pointerdown` rather than by the click handler
 * alone. A drag started on the grip often produces no click on the card at all
 * — the grip stops it, and a release over another column retargets it to an
 * ancestor — so a flag waiting to be consumed there would survive the drop and
 * swallow the next genuine click instead.
 */
let suppressNextClick = false;

const startDrag = (task: TeamTask, columnIndex: number, event: PointerEvent) => {
    suppressNextClick = false;

    if (!draggable) {
        return;
    }

    // Capture keeps the moves coming when the pointer leaves the card. It
    // throws on an inactive pointer id, which must not abort the drag itself.
    try {
        (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    } catch {
        // Without capture the drag still works while the pointer stays inside.
    }

    // Measured from the real card so the ghost keeps its width and stays
    // under the exact point that was grabbed, instead of jumping to the
    // cursor's corner.
    const card = (event.target as HTMLElement).closest('article')?.getBoundingClientRect();

    drag.value = {
        task,
        originColumn: columnIndex,
        targetColumn: columnIndex,
        insertIndex: 0,
        moved: false,
        startX: event.clientX,
        startY: event.clientY,
        pointerX: event.clientX,
        pointerY: event.clientY,
        offsetX: card ? event.clientX - card.left : 0,
        offsetY: card ? event.clientY - card.top : 0,
        width: card?.width ?? 260
    };
};

const moveDrag = (event: PointerEvent) => {
    if (!drag.value) {
        return;
    }

    const travelled = Math.hypot(event.clientX - drag.value.startX, event.clientY - drag.value.startY);

    drag.value.pointerX = event.clientX;
    drag.value.pointerY = event.clientY;
    drag.value.targetColumn = columnIndexAt(event.clientX, drag.value.originColumn);
    drag.value.insertIndex = insertionIndexAt(drag.value.targetColumn, event.clientY, drag.value.task.id);

    if (travelled >= DRAG_THRESHOLD_PX) {
        drag.value.moved = true;
    }
};

const endDrag = () => {
    const current = drag.value;

    drag.value = null;

    if (!current?.moved) {
        return;
    }

    suppressNextClick = true;

    const target = columns.value[current.targetColumn];

    if (!target?.droppable) {
        return;
    }

    // The column's whole content, the card slotted where it was dropped. Same
    // payload whether the drop crossed columns or only moved a card up inside
    // its own, so both go through one path on the server.
    const taskIds = target.tasks
        .filter(task => task.id !== current.task.id)
        .map(task => task.id);

    taskIds.splice(current.insertIndex, 0, current.task.id);

    const unchanged = current.targetColumn === current.originColumn
        && taskIds.every((id, index) => target.tasks[index]?.id === id);

    if (unchanged) {
        return;
    }

    emits('reorder', {status: target.status, taskIds});
};

/**
 * Move, release and cancel are watched on the window, not on the card:
 * pointer capture can be refused, and a release landing anywhere but the card
 * would then leave the drag state set, freezing the board in its preview.
 */
useEventListener(window, 'pointerdown', () => {
    suppressNextClick = false;
});
useEventListener(window, 'pointermove', moveDrag);
useEventListener(window, 'pointerup', endDrag);
useEventListener(window, 'pointercancel', () => {
    drag.value = null;
});

/**
 * The chevrons resolve their target from the card's own status, exactly like
 * the card resolves whether they are enabled. Reading it from the column index
 * would run off the end of the known columns on a stray one.
 */
const advance = (task: TeamTask) => {
    const status = nextStatus(task.status);

    if (status) {
        emits('move', {task, status});
    }
};

const recede = (task: TeamTask) => {
    const status = previousStatus(task.status);

    if (status) {
        emits('move', {task, status});
    }
};

const handleCardClick = (task: TeamTask) => {
    if (suppressNextClick) {
        suppressNextClick = false;

        return;
    }

    emits('open', task);
};

/**
 * Position of the card following the pointer.
 *
 * A composited transform rather than left/top: this recomputes on every
 * pointermove and must not force a layout each frame.
 */
const ghostStyle = computed(() => {
    if (!drag.value) {
        return {};
    }

    const x = drag.value.pointerX - drag.value.offsetX;
    const y = drag.value.pointerY - drag.value.offsetY;

    return {
        transform: `translate3d(${x}px, ${y}px, 0) rotate(2deg)`,
        width: `${drag.value.width}px`
    };
});

/**
 * Where the insertion line is drawn. The slot is counted among the cards that
 * stay put, so the dragged card is skipped when walking the column.
 */
const slotBefore = (columnIndex: number, cardIndex: number): number =>
    (columns.value[columnIndex]?.tasks ?? [])
        .slice(0, cardIndex)
        .filter(task => task.id !== drag.value?.task.id)
        .length;

const showsInsertion = (columnIndex: number): boolean =>
    !!drag.value && drag.value.moved && drag.value.targetColumn === columnIndex
    && !!columns.value[columnIndex]?.droppable;

const isInsertionSlot = (columnIndex: number, cardIndex: number, taskId: number): boolean =>
    showsInsertion(columnIndex)
    && taskId !== drag.value?.task.id
    && slotBefore(columnIndex, cardIndex) === drag.value?.insertIndex;

const isTrailingSlot = (columnIndex: number): boolean => {
    if (!showsInsertion(columnIndex)) {
        return false;
    }

    const remaining = (columns.value[columnIndex]?.tasks ?? [])
        .filter(task => task.id !== drag.value?.task.id).length;

    return drag.value?.insertIndex === remaining;
};

const isDropTarget = (index: number) =>
    !!drag.value
    && drag.value.moved
    && drag.value.targetColumn === index
    && drag.value.originColumn !== index
    && !!columns.value[index]?.droppable;
</script>

<template>
  <div class="grid grid-cols-1 gap-3 md:grid-cols-3" :class="{'select-none': !!drag}">
    <section
      v-for="(column, index) in columns"
      :key="column.status"
      :ref="(element) => setColumnElement(element, index)"
      class="flex flex-col rounded-xl border bg-gray-50/70 p-2 transition"
      :class="isDropTarget(index) ? 'border-primary bg-primary/5' : 'border-gray-200'"
    >
      <header class="mb-2 flex items-center gap-2 px-1">
        <h2 class="text-xs font-semibold text-gray-700">{{ column.label }}</h2>
        <span class="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
          {{ column.tasks.length }}
        </span>
        <button
          v-if="column.status === COMPLETED_STATUS && column.tasks.length"
          class="ml-auto flex size-5 items-center justify-center rounded text-gray-400 hover:bg-white hover:text-gray-700"
          title="Archiver toutes les tâches terminées"
          @click="emits('archive-completed')"
        >
          <Archive class="size-3.5" />
        </button>
      </header>

      <div class="flex flex-col gap-2">
        <template v-for="(task, cardIndex) in column.tasks" :key="task.id">
          <div v-if="isInsertionSlot(index, cardIndex, task.id)" class="h-0.5 rounded-full bg-primary" />
          <div :ref="(element) => setCardElement(task.id, element)">
            <TeamTaskCard
              :task="task"
              :members="members"
              :draggable="draggable"
              :class="{'opacity-40': drag?.moved && drag.task.id === task.id}"
              @grab="startDrag(task, index, $event)"
              @open="handleCardClick(task)"
              @claim="emits('claim', task)"
              @advance="advance(task)"
              @recede="recede(task)"
            />
          </div>
        </template>

        <div v-if="isTrailingSlot(index)" class="h-0.5 rounded-full bg-primary" />

        <!-- Not in the design, which never shows an empty board: without it a
             column with nothing in it reads as broken rather than as done. -->
        <p v-if="!column.tasks.length" class="px-1 py-4 text-center text-[11px] text-gray-400">
          Aucune tâche
        </p>
      </div>
    </section>
  </div>

  <!--
    Teleported so no ancestor transform can turn the fixed positioning into
    something relative, and inert so the ghost never steals the pointer it is
    following.
  -->
  <Teleport to="body">
    <div
      v-if="drag?.moved"
      class="pointer-events-none fixed left-0 top-0 z-50 opacity-95 drop-shadow-xl"
      :style="ghostStyle"
    >
      <TeamTaskCard :task="drag.task" :members="members" />
    </div>
  </Teleport>
</template>
